import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  FileText, Upload, Trash2, Loader2, CheckCircle2, AlertTriangle, Clock,
  ScanLine, Search, RefreshCw, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { useToast } from '../../../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  parsed:    { icon: ScanLine,      label: 'Parsed',    cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  reviewed:  { icon: Clock,         label: 'Reviewed',  cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  confirmed: { icon: CheckCircle2,  label: 'Confirmed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  failed:    { icon: AlertTriangle, label: 'Failed',    cls: 'bg-red-50 text-red-700 border-red-200' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.parsed;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${s.cls}`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </Badge>
  );
};

const fmt = (n) => new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(n) || 0);

const SupplierOCR = () => {
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const fileInputRef = useRef(null);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchDocs = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/api/procurement/supplier-ocr`, {
        headers: authHeaders,
      });
      setDocs(r.data || []);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to load OCR documents',
      });
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({ variant: 'destructive', title: 'Invalid file', description: 'Only PDF files are supported.' });
      return;
    }
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      const r = await axios.post(`${API_URL}/api/procurement/supplier-ocr/upload`, form, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      toast({
        title: 'Parsed',
        description: `${r.data.line_items?.length || 0} line items extracted from ${file.name}`,
      });
      await fetchDocs();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: err.response?.data?.detail || 'Failed to parse document',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this OCR document?')) return;
    try {
      await axios.delete(`${API_URL}/api/procurement/supplier-ocr/${id}`, { headers: authHeaders });
      setDocs((prev) => prev.filter((d) => d.id !== id));
      toast({ title: 'Deleted', description: 'Document removed.' });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Error', description: err.response?.data?.detail || 'Failed' });
    }
  };

  const filtered = docs.filter((d) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [
      d.file_name, d.document_number, d.supplier?.company_name, d.status,
    ].some((v) => String(v || '').toLowerCase().includes(s));
  });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="supplier-ocr-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
            <ScanLine className="h-7 w-7 text-[#D97706]" />
            Supplier OCR Intake
          </h1>
          <p className="text-sm text-[#6C757D] mt-1">
            Drop supplier invoices or proposals (PDF) to auto-extract line items into Suppliers &amp; Inventory.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDocs} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            className="bg-[#D97706] hover:bg-[#92400E]"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Parsing…' : 'Upload PDF'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            hidden
            onChange={(e) => handleUpload(e.target.files?.[0])}
            data-testid="file-input"
          />
        </div>
      </div>

      {/* Dropzone */}
      <Card
        className="mb-6 border-dashed border-2 border-amber-300 bg-amber-50/40"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) handleUpload(f);
        }}
      >
        <CardContent className="py-10 text-center">
          <Upload className="mx-auto h-10 w-10 text-amber-600 mb-3" />
          <p className="text-sm text-amber-900 font-medium">
            Drag &amp; drop a PDF here, or click <span className="underline cursor-pointer"
              onClick={() => fileInputRef.current?.click()}>browse</span>
          </p>
          <p className="text-xs text-amber-700 mt-1">Max 10 MB · GPT-4o-mini parses the text automatically</p>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="mb-4">
        <CardContent className="pt-4 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by file name, supplier or document number…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader><CardTitle className="text-base">Parsed Documents</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-500">
              <FileText className="mx-auto w-10 h-10 mb-2 text-gray-300" />
              No documents yet. Upload a supplier PDF above.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#023E8A] text-white">
                    <th className="p-3 text-left">File</th>
                    <th className="p-3 text-left">Doc #</th>
                    <th className="p-3 text-left">Supplier</th>
                    <th className="p-3 text-center">Items</th>
                    <th className="p-3 text-right">Total</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d, idx) => (
                    <tr key={d.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`doc-row-${d.id}`}>
                      <td className="p-3">
                        <div className="font-medium text-[#2B2D42] truncate max-w-[220px]" title={d.file_name}>
                          {d.file_name}
                        </div>
                        <div className="text-xs text-gray-500">{(d.created_at || '').slice(0, 16).replace('T', ' ')}</div>
                      </td>
                      <td className="p-3 font-mono">{d.document_number || '—'}</td>
                      <td className="p-3">{d.supplier?.company_name || <span className="text-gray-400">Unknown</span>}</td>
                      <td className="p-3 text-center">{d.line_items?.length || 0}</td>
                      <td className="p-3 text-right font-mono">
                        {d.totals?.currency || 'AED'} {fmt(d.totals?.grand_total)}
                      </td>
                      <td className="p-3 text-center"><StatusBadge status={d.status} /></td>
                      <td className="p-3 text-center whitespace-nowrap">
                        <Link to={`/admin/procurement/ocr/${d.id}`}>
                          <Button variant="outline" size="sm" className="mr-2" data-testid={`view-${d.id}`}>
                            <Eye className="w-3 h-3 mr-1" /> Review
                          </Button>
                        </Link>
                        <Button
                          variant="outline" size="sm"
                          className="text-red-700 border-red-200 hover:bg-red-50"
                          onClick={() => handleDelete(d.id)}
                          data-testid={`delete-${d.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SupplierOCR;
